import { tokens } from '@/design';
import type { CSSProperties } from 'react';
import type {
  LayoutTextSymbol,
  TableFrameElement,
  TableFrameField,
  TableFrameRuntimeData,
} from './layoutTypes';

export interface LayoutTableFrameProps {
  readonly element: TableFrameElement;
  readonly data: TableFrameRuntimeData | null;
}

function textCss(symbol: LayoutTextSymbol, scale: number): CSSProperties {
  const bold = symbol.fontStyle === 'bold' || symbol.fontStyle === 'boldItalic';
  const italic =
    symbol.fontStyle === 'italic' || symbol.fontStyle === 'boldItalic';
  const shadow: string[] = [];
  if (symbol.halo.enabled) {
    shadow.push(`0 0 ${symbol.halo.sizePt}px ${symbol.halo.color}`);
  }
  if (symbol.shadow.enabled) {
    shadow.push(
      `${symbol.shadow.offsetXPt}px ${symbol.shadow.offsetYPt}px ${symbol.shadow.color}`,
    );
  }
  return {
    color: symbol.color,
    fontFamily: symbol.fontFamily,
    fontSize: `${symbol.sizePt * scale}pt`,
    fontWeight: bold ? tokens.font.weight.bold : tokens.font.weight.regular,
    fontStyle: italic ? 'italic' : 'normal',
    textDecoration: [
      symbol.underline ? 'underline' : '',
      symbol.strikethrough ? 'line-through' : '',
    ].filter(Boolean).join(' ') || undefined,
    opacity: symbol.opacityPercent / 100,
    textShadow: shadow.join(', ') || undefined,
    WebkitTextStroke:
      symbol.outlineWidthPt > 0
        ? `${symbol.outlineWidthPt}pt ${symbol.outlineColor}`
        : undefined,
  };
}

function formatValue(value: unknown, field: TableFrameField): string {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'number') return String(value);
  if (field.numberFormat === 'integer') return Math.round(value).toLocaleString();
  if (field.numberFormat === 'decimal') {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: field.decimalPlaces,
      maximumFractionDigits: field.decimalPlaces,
    });
  }
  if (field.numberFormat === 'percent') {
    return `${(value * 100).toFixed(field.decimalPlaces)}%`;
  }
  return value.toLocaleString();
}

function compareValues(left: unknown, right: unknown): number {
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left ?? '').localeCompare(String(right ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

function sortedRows(
  rows: readonly Readonly<Record<string, unknown>>[],
  fields: readonly TableFrameField[],
): readonly Readonly<Record<string, unknown>>[] {
  const sorts = fields
    .filter((field) => field.sortDirection !== 'none')
    .sort((left, right) => left.sortOrder - right.sortOrder);
  if (sorts.length === 0) return rows;
  return [...rows].sort((left, right) => {
    for (const field of sorts) {
      const comparison = compareValues(left[field.name], right[field.name]);
      if (comparison !== 0) {
        return field.sortDirection === 'descending' ? -comparison : comparison;
      }
    }
    return 0;
  });
}

export function LayoutTableFrame({ element, data }: LayoutTableFrameProps) {
  if (!element.visible) return null;
  const fields = element.fields.filter((field) => field.visible);
  const rows = sortedRows(data?.rows ?? [], fields).slice(0, element.maximumRows);
  const estimatedRowsPerColumn = Math.max(
    1,
    Math.floor(element.rect.height / Math.max(5, element.rowTextSymbol.sizePt * 0.55)),
  );
  const columns =
    element.fittingStrategy === 'adjustColumns'
    || element.fittingStrategy === 'adjustColumnsAndFont'
      ? Math.max(1, Math.min(4, Math.ceil(rows.length / estimatedRowsPerColumn)))
      : 1;
  const fontScale =
    element.fittingStrategy === 'adjustFontSize'
    || element.fittingStrategy === 'adjustColumnsAndFont'
      ? Math.max(
          element.minimumFontSizePt / element.rowTextSymbol.sizePt,
          Math.min(1, estimatedRowsPerColumn / Math.max(1, rows.length / columns)),
        )
      : 1;
  const capacity =
    element.fittingStrategy === 'adjustFrame'
      ? rows.length
      : estimatedRowsPerColumn * columns;
  const shownRows = rows.slice(0, capacity);
  const overflowing = shownRows.length < rows.length;
  const rowsPerColumn = element.balanceColumns
    ? Math.ceil(shownRows.length / columns)
    : estimatedRowsPerColumn;
  const chunks = Array.from({ length: columns }, (_, column) =>
    shownRows.slice(column * rowsPerColumn, (column + 1) * rowsPerColumn),
  ).filter((chunk) => chunk.length > 0);

  return (
    <div
      data-table-frame-style={element.styleId}
      data-table-frame-columns={chunks.length}
      data-table-frame-overflowing={overflowing}
      style={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.max(1, chunks.length)}, minmax(0, 1fr))`,
        gap: `${element.frameColumnGapPt}pt`,
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        overflow: element.fittingStrategy === 'adjustFrame' ? 'auto' : 'hidden',
        padding: `${element.frame.gapYPt}pt ${element.frame.gapXPt}pt`,
        background: element.frame.enabled ? element.frame.color : 'transparent',
        border:
          element.frame.enabled && element.frame.outlineWidthPt > 0
            ? `${element.frame.outlineWidthPt}pt solid ${element.frame.outlineColor}`
            : 'none',
        boxShadow:
          element.frame.enabled
          && (element.frame.offsetXPt !== 0 || element.frame.offsetYPt !== 0)
            ? `${element.frame.offsetXPt}pt ${element.frame.offsetYPt}pt ${element.frame.color}`
            : 'none',
      }}
    >
      {data?.loading ? (
        <span style={{ fontSize: tokens.font.size.xs }}>Loading table…</span>
      ) : data?.error ? (
        <span role="alert" style={{ color: tokens.color.semantic.danger }}>
          {data.error}
        </span>
      ) : fields.length === 0 ? (
        <span style={{ fontSize: tokens.font.size.xs }}>
          Select a layer with attribute fields
        </span>
      ) : shownRows.length === 0 ? (
        <span style={{ fontSize: tokens.font.size.xs }}>No records</span>
      ) : (
        chunks.map((chunk, chunkIndex) => (
          <table
            key={chunkIndex}
            style={{
              width: '100%',
              height: 'fit-content',
              borderCollapse: 'collapse',
              tableLayout: 'fixed',
            }}
          >
            {element.showHeading ? (
              <thead>
                <tr>
                  {fields.map((field) => (
                    <th
                      key={field.name}
                      scope="col"
                      style={{
                        ...textCss(element.headingTextSymbol, fontScale),
                        width: `${field.widthPt}pt`,
                        padding: tokens.space[1],
                        color: element.headingTextSymbol.color,
                        background: element.headingBackgroundColor,
                        border: `${tokens.border.width.thin}px solid ${element.headingBorderColor}`,
                        borderBottom: `${tokens.border.width.thin}px solid ${element.headingUnderlineColor}`,
                        textAlign: field.headingAlignment,
                        whiteSpace: field.wrapHeading ? 'normal' : 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {field.alias}
                    </th>
                  ))}
                </tr>
              </thead>
            ) : null}
            <tbody>
              {chunk.map((row, rowIndex) => {
                const cycle =
                  Math.max(1, element.rowBackground1Count)
                  + Math.max(1, element.rowBackground2Count);
                const first =
                  (rowIndex + chunkIndex * rowsPerColumn) % cycle
                  < Math.max(1, element.rowBackground1Count);
                return (
                  <tr key={`${chunkIndex}-${rowIndex}`}>
                    {fields.map((field) => (
                      <td
                        key={field.name}
                        style={{
                          ...textCss(element.rowTextSymbol, fontScale),
                          padding: tokens.space[1],
                          background: first
                            ? element.rowBackground1
                            : element.rowBackground2,
                          border: `${tokens.border.width.thin}px solid ${element.rowBorderColor}`,
                          textAlign: field.alignment,
                          whiteSpace: field.wrapValues ? 'normal' : 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {formatValue(row[field.name], field)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        ))
      )}
      {overflowing ? (
        <span
          role="status"
          aria-label="Table frame contents overflow"
          style={{
            position: 'absolute',
            right: tokens.space[1],
            bottom: tokens.space[1],
            color: tokens.color.semantic.danger,
            fontWeight: tokens.font.weight.bold,
          }}
        >
          …
        </span>
      ) : null}
    </div>
  );
}
